import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Typography, Paper, Grid, Stack, Card, CardContent, Divider, Tabs, Tab } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import { reportService } from '@/services/reportService'
import MultiPeriodComparator from '@/components/comparators/MultiPeriodComparator'

const Comparators = () => {
  const [tabValue, setTabValue] = useState(0)
  const [leftStart, setLeftStart] = useState<Date | null>(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))
  const [leftEnd, setLeftEnd] = useState<Date | null>(new Date())
  const [rightStart, setRightStart] = useState<Date | null>(new Date(Date.now() - 14 * 24 * 60 * 60 * 1000))
  const [rightEnd, setRightEnd] = useState<Date | null>(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000))

  const [comparison, setComparison] = useState<{ left: { deposits: number; withdrawals: number; net: number }; right: { deposits: number; withdrawals: number; net: number } }>({
    left: { deposits: 0, withdrawals: 0, net: 0 },
    right: { deposits: 0, withdrawals: 0, net: 0 },
  })

  const canCompare = leftStart && leftEnd && rightStart && rightEnd

  const loadComparison = useCallback(async () => {
    if (!canCompare) return
    const params = {
      left_period_start: leftStart!.toISOString(),
      left_period_end: leftEnd!.toISOString(),
      right_period_start: rightStart!.toISOString(),
      right_period_end: rightEnd!.toISOString(),
    }
    const data = await reportService.comparePeriods(params)
    setComparison(data)
  }, [canCompare, leftStart, leftEnd, rightStart, rightEnd])

  useEffect(() => {
    loadComparison()
  }, [loadComparison])

  const differences = useMemo(() => {
    const depositDiff = comparison.left.deposits - comparison.right.deposits
    const withdrawalDiff = comparison.left.withdrawals - comparison.right.withdrawals
    const netDiff = comparison.left.net - comparison.right.net
    return {
      deposits: depositDiff,
      withdrawals: withdrawalDiff,
      net: netDiff,
      depositPct: comparison.right.deposits ? (depositDiff / comparison.right.deposits) * 100 : 0,
      withdrawalPct: comparison.right.withdrawals ? (withdrawalDiff / comparison.right.withdrawals) * 100 : 0,
      netPct: comparison.right.net ? (netDiff / comparison.right.net) * 100 : 0,
    }
  }, [comparison])

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Comparators
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Two Period Comparison" />
          <Tab label="Multi-Period Comparison" />
        </Tabs>
      </Paper>

      {tabValue === 1 && <MultiPeriodComparator />}

      {tabValue === 0 && (
        <>

      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Typography variant="h6" gutterBottom>
                Period 1
              </Typography>
              <Stack spacing={2}>
                <DatePicker
                  label="Start Date"
                  value={leftStart}
                  onChange={setLeftStart}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label="End Date"
                  value={leftEnd}
                  onChange={setLeftEnd}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Stack>
            </Grid>
            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CompareArrowsIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="h6" gutterBottom>
                Period 2
              </Typography>
              <Stack spacing={2}>
                <DatePicker
                  label="Start Date"
                  value={rightStart}
                  onChange={setRightStart}
                  slotProps={{ textField: { fullWidth: true } }}
                />
                <DatePicker
                  label="End Date"
                  value={rightEnd}
                  onChange={setRightEnd}
                  slotProps={{ textField: { fullWidth: true } }}
                />
              </Stack>
            </Grid>
          </Grid>
        </Paper>
      </LocalizationProvider>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Period 1 Summary
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Deposits
                  </Typography>
                  <Typography variant="h5">KES {comparison.left.deposits.toFixed(2)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Withdrawals
                  </Typography>
                  <Typography variant="h5">KES {comparison.left.withdrawals.toFixed(2)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Net Flow
                  </Typography>
                  <Typography
                    variant="h5"
                    color={comparison.left.net >= 0 ? 'success.main' : 'error.main'}
                  >
                    KES {comparison.left.net.toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Period 2 Summary
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Deposits
                  </Typography>
                  <Typography variant="h5">KES {comparison.right.deposits.toFixed(2)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Withdrawals
                  </Typography>
                  <Typography variant="h5">KES {comparison.right.withdrawals.toFixed(2)}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Net Flow
                  </Typography>
                  <Typography
                    variant="h5"
                    color={comparison.right.net >= 0 ? 'success.main' : 'error.main'}
                  >
                    KES {comparison.right.net.toFixed(2)}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Differences (Period 1 - Period 2)
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Deposits Difference
                    </Typography>
                    <Typography
                      variant="h5"
                      color={differences.deposits >= 0 ? 'success.main' : 'error.main'}
                    >
                      KES {differences.deposits.toFixed(2)} ({differences.depositPct.toFixed(1)}%)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Withdrawals Difference
                    </Typography>
                    <Typography
                      variant="h5"
                      color={differences.withdrawals >= 0 ? 'success.main' : 'error.main'}
                    >
                      KES {differences.withdrawals.toFixed(2)} ({differences.withdrawalPct.toFixed(1)}%)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Net Flow Difference
                    </Typography>
                    <Typography
                      variant="h5"
                      color={differences.net >= 0 ? 'success.main' : 'error.main'}
                    >
                      KES {differences.net.toFixed(2)} ({differences.netPct.toFixed(1)}%)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
        </>
      )}
    </Box>
  )
}

export default Comparators

